import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { BodyMetricType } from "../../../enums";
import { useApiMutation } from "../../../infrastructure/hooks/useApiMutation";
import { IBodyMetricsDataSource } from "../models/bodyMetricModels";
import {
  addBodyMetricFormSchema,
  getAddBodyMetricFormDefaultValues,
  IAddBodyMetricFormModel,
} from "../models/addBodyMetricFormModel";
import { BODY_METRIC_TYPES } from "../models/bodyMetricTypes";
import { toApiDate } from "../utils/bodyMetricsUtility";

interface IUseAddBodyMetricDialogProps {
  visible: boolean;
  /** Skips the "what are you measuring" step (the "+" inside a metric's details). */
  presetType: BodyMetricType | null;
  dataSource: IBodyMetricsDataSource;
  onHide: () => void;
}

/**
 * The "+" flow: pick what to measure, then type the number and the day. Saves through
 * the data source's add endpoint and refreshes its list.
 */
export const useAddBodyMetricDialog = ({
  visible,
  presetType,
  dataSource,
  onHide,
}: IUseAddBodyMetricDialogProps) => {
  const form = useForm<IAddBodyMetricFormModel>({
    defaultValues: getAddBodyMetricFormDefaultValues(presetType),
    resolver: yupResolver(addBodyMetricFormSchema),
  });
  // The metric is picked with the tiles, not an input. A preset (the "+" inside a metric's
  // details) wins from the first render, so the picker never flashes; the form keeps a
  // copy of the type for validation and the payload.
  const [pickedType, setPickedType] = useState<BodyMetricType | null>(null);
  const selectedType = presetType ?? pickedType;
  const config = selectedType ? BODY_METRIC_TYPES[selectedType] : null;

  useEffect(() => {
    if (visible) {
      form.reset(getAddBodyMetricFormDefaultValues(presetType));
    } else {
      setPickedType(null);
    }
  }, [visible, presetType, form]);

  const selectType = (type: BodyMetricType | null) => {
    setPickedType(type);
    form.setValue("type", type as BodyMetricType);
    form.clearErrors("value");
  };

  const { mutate, isLoading } = useApiMutation();

  const handleSubmit = form.handleSubmit((values: IAddBodyMetricFormModel) => {
    mutate(
      {
        endpoint: dataSource.addEndpoint,
        method: "post",
        // The API answers with the saved entry, not a message.
        successMessage: "Метриката е записана.",
        payload: {
          ...dataSource.extraPayload,
          type: values.type,
          value: values.value,
          measuredOn: toApiDate(values.measuredOn),
        },
        invalidateKeys: [dataSource.listQueryKey],
      },
      { onSuccess: onHide },
    );
  });

  const { errors } = form.formState;
  const errorMessage = errors.value?.message ?? errors.measuredOn?.message;

  return {
    form,
    errorMessage,
    selectedType,
    config,
    selectType,
    isLoading,
    handleSubmit,
  };
};
